import { tuningBadge } from '../../data/tunings.js';
import React from 'react';
import { Music, Download, Check } from 'lucide-react';
import { Button } from '../ui/Button.js';
import { TabSong } from '../../types/index.js';
import { DifficultySummary } from '../tab/DifficultyMeter.js';
import { toPersianDigits } from '../../utils/timeUtils.js';

export interface MarketTabRowProps {
  tab: TabSong;
  isDownloaded: boolean;
  isDownloading?: boolean;
  onDownload: (tab: TabSong) => void;
  onOpen: (tabId: string) => void;
}

const formatTuningDisplay = (tuning?: string): string => tuningBadge(tuning);

export const MarketTabRow: React.FC<MarketTabRowProps> = ({
  tab,
  isDownloaded,
  isDownloading = false,
  onDownload,
  onOpen
}) => {
  const displayTuning = formatTuningDisplay(tab.tuning);
  const trackCount = tab.tracks && tab.tracks.length > 0 ? tab.tracks.length : null;
  const tracksText = trackCount !== null ? `${toPersianDigits(trackCount)} ترک` : null;

  return (
    <div
      data-testid={`market-tab-row-${tab.id}`}
      className="flex items-center gap-3 md:gap-3.5 px-3.5 md:px-[18px] py-3 md:py-3.5 border-b border-base-300 last:border-b-0 transition-colors hover:bg-base-200/40"
    >
      {/* 40x40 Music note tile (desktop only) */}
      <span
        aria-hidden="true"
        className="hidden md:flex size-10 shrink-0 rounded-[10px] bg-neutral text-primary-soft items-center justify-center border border-base-300"
      >
        <Music className="size-4.5" />
      </span>

      {/* Song title & Artist / Subtitle */}
      <div className="flex-1 min-w-0">
        <p
          dir="ltr"
          className="text-[15px] md:text-sm font-semibold text-base-content text-right truncate"
        >
          {tab.title}
        </p>

        {/* Subtitle: artist · tracks (desktop) / tuning (mobile) */}
        <p className="text-xs text-base-content/60 mt-0.5 truncate text-start">
          <span dir="ltr">{tab.artist}</span>
          {tracksText && (
            <span className="hidden md:inline"> · {tracksText}</span>
          )}
          <span className="md:hidden inline"> · </span>
          <span dir="ltr" className="md:hidden inline font-mono text-base-content/80">
            {displayTuning}
          </span>
        </p>
        <DifficultySummary
          rhythm={tab.rhythmDifficulty}
          lead={tab.leadDifficulty}
          size="sm"
          className="mt-1 max-w-full flex-wrap"
        />
      </div>

      {/* Tuning Chip (desktop only) */}
      <span
        dir="ltr"
        className="hidden md:inline-flex items-center h-6 px-2.5 rounded-full bg-neutral border border-base-300 font-mono text-[11px] text-neutral-content/90 shrink-0 select-none"
      >
        {displayTuning}
      </span>

      {/* Action CTA: single responsive button for both desktop (w-28 h-9) & mobile (w-11 h-11) */}
      <div className="shrink-0">
        {isDownloaded ? (
          <Button
            variant="ghost"
            size="sm"
            leadingIcon={<Check className="size-4 md:size-3.5" />}
            onClick={() => onOpen(tab.id)}
            className="w-11 md:w-28 h-11 md:h-9 p-0 md:px-3 text-success-soft hover:text-success hover:bg-success/10 font-semibold"
            data-testid={`market-open-button-${tab.id}`}
            aria-label="باز کردن تبلچر"
          >
            <span className="hidden md:inline">باز کردن</span>
          </Button>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            leadingIcon={<Download className="size-5 md:size-3.5 shrink-0" />}
            loading={isDownloading}
            onClick={() => onDownload(tab)}
            className="w-11 md:w-28 h-11 md:h-9 p-0 md:px-3 font-semibold"
            data-testid={`market-download-button-${tab.id}`}
            aria-label="دریافت تبلچر"
          >
            <span className="hidden md:inline">دریافت</span>
            <span className="inline text-[0px] leading-none text-transparent select-none"> آفلاین</span>
          </Button>
        )}
      </div>
    </div>
  );
};
